import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth.store";
import type { LoginRequest, LoginResponse, MeResponse } from "@opus-dt/shared-types";

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: LoginRequest) => {
      const result = await api.post<LoginResponse>("/api/v1/auth/login", input);
      api.setAccessToken(result.accessToken); // token no módulo do api-client (fonte única)
      setSession({ user: { ...result.user } });
      return result;
    },
  });
}

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  return useMutation({
    mutationFn: async () => {
      await api.post("/api/v1/auth/logout");
      api.setAccessToken(null);
      clearSession();
    },
    // Sem tratamento local (AppLayout.tsx só chama .mutate()) — herda o toast automático do
    // MutationCache, só com título mais específico que o default.
    meta: { errorTitle: "Falha ao sair" },
  });
}

export function useMe(enabled = true) {
  const setSession = useAuthStore((s) => s.setSession);
  const clearSession = useAuthStore((s) => s.clearSession);
  const setInitialized = useAuthStore((s) => s.setInitialized);
  return useQuery<MeResponse | null>({
    queryKey: ["auth", "me"],
    enabled,
    queryFn: async () => {
      try {
        const me = await api.get<MeResponse>("/api/v1/auth/me");
        // Após F5, o /auth/me autentica pelo cookie httpOnly `opus_access` e responde 200 SEM passar
        // pelo refresh — então o token de módulo continua nulo. Mas o WebSocket (handshake exige o
        // token, não lê cookie) e o Bearer precisam dele: repõe via /auth/refresh (cookie).
        // DECISÃO (review #3): esse 1 POST /auth/refresh extra no boot é INTENCIONAL. A alternativa
        // (o GET /auth/me cunhar e devolver um token) foi descartada — é um side effect num GET, com
        // churn no contrato de auth, por um ganho negligível. O custo aqui é 1 round-trip por carga.
        if (!api.getAccessToken()) await api.refresh();
        setSession({ user: me });
        return me;
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) {
          clearSession();
          return null;
        }
        throw err;
      } finally {
        setInitialized(true);
      }
    },
    retry: false,
  });
}
